import React, { memo, useEffect, useRef } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types'

import { TURN_STATUS } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import FailedState from './Components/FailedState/FailedState'
import GeneratingState from './Components/GeneratingState/GeneratingState'
import Result from './Components/Result/Result'
import ResultGrid from './Components/ResultGrid/ResultGrid'
import SentMessage from './Components/SentMessage/SentMessage'

function Conversation({
  messages, isGenerating, onApply, onRegenerate, onGenerateAgain, onEdit, isFullWidth = false,
}) {
  const t = useTranslation()
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  if (messages.length === 0) {
    return (
      <div
        className={classnames(
          'image-assistant-create-tab__create-empty',
          isFullWidth && 'image-assistant-create-tab__create-empty--full-width',
        )}
      >
        <p
          className={classnames(
            'image-assistant-create-tab__create-empty-text',
            isFullWidth ? 'cgt-heading-300' : 'cgt-heading-50',
          )}
        >
          {t('imageAssistantPanel.createTab.conversation.emptyPrompt')}
        </p>
      </div>
    )
  }

  return (
    <div className="image-assistant-create-tab__conversation">
      {messages.map(message => (
        <div key={message.id} className="image-assistant-create-tab__turn">
          {!message.continuation && <SentMessage prompt={message.prompt} images={message.images} />}

          {message.status === TURN_STATUS.LOADING && <GeneratingState count={message.expectedImages} />}

          {message.status === TURN_STATUS.FAILED && (
            <FailedState onGenerateAgain={() => onGenerateAgain(message)} />
          )}

          {message.status === TURN_STATUS.DONE && message.results.length === 1 && (
            <Result
              result={message.results[0]}
              isGenerating={isGenerating}
              onApply={() => onApply(message.results[0])}
              onRegenerate={() => onRegenerate(message)}
              onEdit={() => onEdit(message.results[0])}
            />
          )}

          {message.status === TURN_STATUS.DONE && message.results.length > 1 && (
            <ResultGrid
              results={message.results}
              isGenerating={isGenerating}
              onApply={onApply}
              onRegenerate={() => onRegenerate(message)}
              onEdit={onEdit}
            />
          )}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  )
}

Conversation.propTypes = {
  messages: arrayOf(shape({
    id: number.isRequired,
    prompt: string,
    images: arrayOf(shape({ id: number, url: string })).isRequired,
    status: oneOf([TURN_STATUS.LOADING, TURN_STATUS.DONE, TURN_STATUS.FAILED]).isRequired,
    results: arrayOf(shape({ id: number, url: string, alt: string })).isRequired,
    expectedImages: number,
    continuation: bool,
  })).isRequired,
  isGenerating: bool.isRequired,
  onApply: func.isRequired,
  onRegenerate: func.isRequired,
  onGenerateAgain: func.isRequired,
  onEdit: func.isRequired,
  isFullWidth: bool,
}

export default memo(Conversation)
